/**
 * Browser-side OpenAI-compatible chat-completions streamer. Powers the
 * NVIDIA NIM and Groq paths in the PWA. Mirrors the desktop's
 * `src/main/providers/openai-compatible.ts` factory, but runs in the
 * browser via fetch + SSE parsing — no Node dependencies, no Anthropic
 * SDK.
 *
 * All requests go through same-origin proxy paths (`/api/nvidia`,
 * `/api/groq`) defined in `vite.pwa.config.ts` to sidestep any CORS
 * surprises and make the production deployment story identical to the
 * Anthropic proxy (single serverless edge function pattern).
 */
import type { ClaudeStartArgs } from '../../shared/ipc';
import { keys } from './keystore';
import { buildSystemPrompt, type CoachContext, type CoachMode } from './system-prompt';

type OAIPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } };

type OAIMessage =
  | { role: 'system'; content: string }
  | { role: 'user'; content: string | OAIPart[] }
  | { role: 'assistant'; content: string };

type OAIStreamChunk = {
  choices?: Array<{
    delta?: { content?: string | null };
    finish_reason?: string | null;
  }>;
};

interface Conversation {
  id: string;
  systemPrompt: string;
  history: { role: 'user' | 'assistant'; content: string }[];
  inflight: Map<string, AbortController>;
}

const conversations = new Map<string, Conversation>();

export interface ProviderConfig {
  /** Stable id used in friendly errors and the keys map. */
  id: 'nvidia' | 'groq';
  /** Same-origin proxy URL — `/api/nvidia` or `/api/groq`. */
  baseUrl: string;
  /** Display name surfaced in 401 / 403 messages. */
  displayName: string;
  /** Where the user grabs a key — surfaced in errors. */
  keyHint: string;
  /** Read the saved API key from the keystore. */
  loadKey: () => string | null;
  /** Default model id when caller doesn't pass one. */
  defaultModel: string;
}

export const NVIDIA_CONFIG: ProviderConfig = {
  id: 'nvidia',
  baseUrl: '/api/nvidia/v1',
  displayName: 'NVIDIA NIM',
  keyHint: 'build.nvidia.com',
  loadKey: () => keys.getNvidia(),
  defaultModel: 'meta/llama-3.3-70b-instruct',
};

export const GROQ_CONFIG: ProviderConfig = {
  id: 'groq',
  baseUrl: '/api/groq/v1',
  displayName: 'Groq',
  keyHint: 'console.groq.com/keys',
  loadKey: () => keys.getGroq(),
  defaultModel: 'llama-3.3-70b-versatile',
};

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function getOrCreateConversation(
  id: string,
  mode: CoachMode,
  context: CoachContext,
): Conversation {
  let convo = conversations.get(id);
  if (!convo) {
    convo = {
      id,
      systemPrompt: buildSystemPrompt(mode, context),
      history: [],
      inflight: new Map(),
    };
    conversations.set(id, convo);
  }
  return convo;
}

function abortAll(convo: Conversation): void {
  for (const ac of convo.inflight.values()) ac.abort();
  convo.inflight.clear();
}

function friendlyError(
  cfg: ProviderConfig,
  status: number | undefined,
  raw: string,
): string {
  if (status === 401) {
    return `${cfg.displayName} rejected the API key. Get a fresh key at ${cfg.keyHint} and paste it in Settings.`;
  }
  if (status === 403) return `${cfg.displayName} 403: ${raw || 'access forbidden — check your account permissions.'}`;
  if (status === 429) return `${cfg.displayName} rate-limited the request. Wait a moment and try again.`;
  if (status) return `${cfg.displayName} ${status}: ${raw}`;
  return raw;
}

/** Emit callbacks the dispatcher passes through — same shape as ClaudeEvent.
 *  The PWA's central api/index.ts owns the actual listeners; this client
 *  just forwards via callbacks the caller injects. */
export interface StreamCallbacks {
  onDelta: (text: string) => void;
  onDone: (fullText: string, cancelled: boolean) => void;
  onError: (message: string) => void;
}

export async function startStream(
  cfg: ProviderConfig,
  args: ClaudeStartArgs,
  cb: StreamCallbacks,
): Promise<void> {
  const trimmed = args.question.trim();
  const images = args.imageJpegs ?? [];
  if (!trimmed && images.length === 0) return;

  const apiKey = cfg.loadKey();
  if (!apiKey) {
    cb.onError(
      `No ${cfg.displayName} API key set. Open Settings → API & Model to add one (free at ${cfg.keyHint}).`,
    );
    return;
  }

  const convo = getOrCreateConversation(
    args.conversationId,
    (args.mode ?? 'auto') as CoachMode,
    {
      resume: args.profile?.resume,
      jobDescription: args.profile?.jobDescription,
      englishLevel: args.profile?.englishLevel,
    },
  );

  // Build user-turn parts. Use a string for plain text (smaller payload,
  // matches the simpler form most OAI servers expect), parts array when
  // images are present.
  const parts: OAIPart[] = [];
  for (const img of images) {
    parts.push({
      type: 'image_url',
      image_url: { url: `data:image/jpeg;base64,${bufferToBase64(img)}` },
    });
  }
  if (trimmed) {
    parts.push({ type: 'text', text: trimmed });
  } else if (images.length > 1) {
    parts.push({
      type: 'text',
      text:
        `These ${images.length} images are sequential parts of ONE scrollable question (top to bottom). ` +
        `Read all of them as one continuous prompt before answering.`,
    });
  } else if (images.length === 1) {
    parts.push({ type: 'text', text: 'Answer the question shown in the image.' });
  }

  const userMessage: OAIMessage =
    images.length === 0
      ? { role: 'user', content: trimmed || '' }
      : { role: 'user', content: parts };

  const messages: OAIMessage[] = [
    { role: 'system', content: convo.systemPrompt },
    ...convo.history.map(
      (m) => ({ role: m.role, content: m.content }) as OAIMessage,
    ),
    userMessage,
  ];

  const abort = new AbortController();
  convo.inflight.set(args.exchangeId, abort);

  let assistantText = '';
  let res: Response;
  try {
    res = await fetch(`${cfg.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        model: args.model || cfg.defaultModel,
        stream: true,
        temperature: 0.4,
        max_tokens: 1024,
        messages,
      }),
      signal: abort.signal,
    });
  } catch (err) {
    convo.inflight.delete(args.exchangeId);
    if ((err as Error).name === 'AbortError') {
      cb.onDone('', true);
      return;
    }
    cb.onError(`${cfg.displayName} network error: ${(err as Error).message}`);
    return;
  }

  if (!res.ok) {
    convo.inflight.delete(args.exchangeId);
    const body = await res.text().catch(() => '');
    cb.onError(friendlyError(cfg, res.status, body || res.statusText));
    return;
  }
  if (!res.body) {
    convo.inflight.delete(args.exchangeId);
    cb.onError(`${cfg.displayName} returned no body.`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let pending = '';

  try {
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });
      // SSE frames are separated by blank lines.
      const frames = pending.split(/\r?\n\r?\n/);
      pending = frames.pop() ?? '';
      for (const frame of frames) {
        for (const line of frame.split(/\r?\n/)) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          try {
            const obj = JSON.parse(payload) as OAIStreamChunk;
            const delta = obj.choices?.[0]?.delta?.content;
            if (typeof delta === 'string' && delta.length > 0) {
              assistantText += delta;
              cb.onDelta(delta);
            }
          } catch {
            // Ignore malformed chunks (keep-alive comments, debug lines).
          }
        }
      }
    }
  } catch (err) {
    convo.inflight.delete(args.exchangeId);
    if ((err as Error).name === 'AbortError') {
      cb.onDone(assistantText, true);
      return;
    }
    cb.onError(`${cfg.displayName} stream error: ${(err as Error).message}`);
    return;
  }

  // Commit history (text-only assistant turn — matches the desktop's cached
  // prefix optimization).
  const trimmedAssistant = assistantText.trim();
  if (trimmedAssistant && trimmedAssistant !== 'SKIP') {
    let committedUser = trimmed;
    if (images.length > 0) {
      const stamp = new Date().toLocaleTimeString();
      const noun = images.length === 1 ? 'screenshot' : `${images.length} screenshots`;
      committedUser = `[${noun} at ${stamp}]`;
      if (trimmed) committedUser += ` ${trimmed}`;
    }
    convo.history.push({ role: 'user', content: committedUser });
    convo.history.push({ role: 'assistant', content: trimmedAssistant });
  }

  convo.inflight.delete(args.exchangeId);
  cb.onDone(assistantText, false);
}

export function cancelStream(
  payload?: { conversationId: string; exchangeId?: string },
): void {
  if (!payload) {
    for (const convo of conversations.values()) abortAll(convo);
    return;
  }
  const convo = conversations.get(payload.conversationId);
  if (!convo) return;
  if (payload.exchangeId) {
    const ctrl = convo.inflight.get(payload.exchangeId);
    if (ctrl) {
      ctrl.abort();
      convo.inflight.delete(payload.exchangeId);
    }
  } else {
    abortAll(convo);
  }
}

export async function testKey(
  cfg: ProviderConfig,
  plaintext: string,
): Promise<void> {
  const key = plaintext.trim() || cfg.loadKey() || '';
  if (!key) {
    throw new Error(`No ${cfg.displayName} API key set. Paste one or save it in Settings first.`);
  }
  const res = await fetch(`${cfg.baseUrl}/chat/completions`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${key}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: cfg.defaultModel,
      max_tokens: 1,
      messages: [{ role: 'user', content: 'ok' }],
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(friendlyError(cfg, res.status, body || res.statusText));
  }
}
